/**
 * Circle to ask, the reading half: what a loop drawn on the screen encloses, as lines of text the assistant can read
 * (server: ai/explain.ts). Text is read off the page as it is shown, row by row, with table cells kept apart; a chart
 * marked `data-explain` (components/charts.tsx) contributes its figures rather than its axis labels.
 */

export interface Pt {
  x: number;
  y: number;
}

export interface Box {
  x: number;
  y: number;
  w: number;
  h: number;
}

export interface RegionLine {
  id: number;
  text: string;
  /** Where the line is on screen, in viewport coordinates at the moment it was circled. */
  box: Box;
}

export interface Region {
  page: { path: string; title: string };
  /** The page title, card titles and selected tabs around the circled area, outermost first. */
  headings: string[];
  lines: RegionLine[];
  bounds: Box;
}

const MAX_LINES = 120;
const MAX_CHARS = 10_000;

export function boundsOf(pts: Pt[]): Box {
  let x0 = Infinity;
  let y0 = Infinity;
  let x1 = -Infinity;
  let y1 = -Infinity;
  for (const p of pts) (x0 = Math.min(x0, p.x)), (y0 = Math.min(y0, p.y)), (x1 = Math.max(x1, p.x)), (y1 = Math.max(y1, p.y));
  return { x: x0, y: y0, w: x1 - x0, h: y1 - y0 };
}

/**
 * Is `p` inside the loop? Nonzero winding, so a hand loop that runs on past where it began still holds everything it
 * went round, including the part it went round twice.
 */
export function inside(p: Pt, poly: Pt[]): boolean {
  let wind = 0;
  for (let i = 0; i < poly.length; i++) {
    const a = poly[i]!;
    const b = poly[(i + 1) % poly.length]!;
    const side = (b.x - a.x) * (p.y - a.y) - (p.x - a.x) * (b.y - a.y);
    if (a.y <= p.y) {
      if (b.y > p.y && side > 0) wind++;
    } else if (b.y <= p.y && side < 0) wind--;
  }
  return wind !== 0;
}

/** The share of a box that lies inside the polygon, sampled on a 5 by 5 grid. */
function coverage(r: DOMRect, poly: Pt[]) {
  let n = 0;
  for (let i = 0; i < 5; i++) for (let j = 0; j < 5; j++) if (inside({ x: r.left + (r.width * (i + 0.5)) / 5, y: r.top + (r.height * (j + 0.5)) / 5 }, poly)) n++;
  return n / 25;
}

const overlaps = (r: DOMRect, b: Box) => r.right >= b.x && r.left <= b.x + b.w && r.bottom >= b.y && r.top <= b.y + b.h;
const visible = (el: Element) => (el as Element & { checkVisibility?: (o: object) => boolean }).checkVisibility?.({ opacityProperty: true, visibilityProperty: true }) ?? true;
const clean = (s: string) => s.replace(/\s+/g, ' ').trim();

type Rect = Pick<DOMRect, 'left' | 'top' | 'right' | 'bottom' | 'width' | 'height'>;

interface Piece {
  text: string;
  r: Rect;
  el?: Element;
  /** The card, table row or list item it belongs to: each is read on its own. */
  block?: Element | null;
  /** A chart's summary: kept on a line of its own. */
  whole?: boolean;
}

const BLOCK = 'tr, li, dl > div, .glass, section, [data-explain]';

/** What the polygon encloses, read off the page. `skip` is the circle's own overlay and card. */
export function readRegion(poly: Pt[], skip: (el: Element) => boolean): Region {
  const bounds = boundsOf(poly);
  const pieces: Piece[] = [];

  // Charts first: their figures say more than the axis labels drawn inside them.
  const charts: Element[] = [];
  for (const el of document.querySelectorAll('[data-explain]')) {
    if (skip(el) || !visible(el)) continue;
    const r = el.getBoundingClientRect();
    if (!r.width || !overlaps(r, bounds)) continue;
    if (inside({ x: r.left + r.width / 2, y: r.top + r.height / 2 }, poly) || coverage(r, poly) >= 0.25) {
      charts.push(el);
      pieces.push({ text: clean(el.getAttribute('data-explain') ?? ''), r, el, block: el, whole: true });
    }
  }

  const walker = document.createTreeWalker(document.body, NodeFilter.SHOW_ELEMENT | NodeFilter.SHOW_TEXT, {
    acceptNode(node) {
      if (node.nodeType === Node.TEXT_NODE) return node.nodeValue?.trim() ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_SKIP;
      const el = node as Element;
      if (skip(el) || charts.includes(el) || /^(SCRIPT|STYLE|NOSCRIPT|TEMPLATE)$/.test(el.tagName) || el.getAttribute('aria-hidden') === 'true') return NodeFilter.FILTER_REJECT;
      const r = el.getBoundingClientRect();
      // A whole subtree outside the loop is skipped; an element with no size (display: contents) is looked inside.
      if (r.width && r.height && !overlaps(r, bounds)) return NodeFilter.FILTER_REJECT;
      if (el instanceof HTMLInputElement || el instanceof HTMLSelectElement || el instanceof HTMLTextAreaElement) {
        const value = el instanceof HTMLSelectElement ? (el.selectedOptions[0]?.text ?? '') : el.type === 'password' || el.type === 'hidden' ? '' : el.value;
        if (clean(value) && r.width && inside({ x: r.left + r.width / 2, y: r.top + r.height / 2 }, poly)) pieces.push({ text: clean(value), r, el, block: el.closest(BLOCK) });
        return NodeFilter.FILTER_REJECT;
      }
      return NodeFilter.FILTER_SKIP;
    },
  });
  const range = document.createRange();
  for (let n = walker.nextNode(); n; n = walker.nextNode()) {
    const el = n.parentElement;
    if (!el || !visible(el)) continue;
    range.selectNodeContents(n);
    // A wrapped paragraph has one box per line: keep the node when any of them sits inside the loop.
    const hit = [...range.getClientRects()].find((r) => r.width > 0 && r.height > 0 && inside({ x: r.left + r.width / 2, y: r.top + r.height / 2 }, poly));
    if (hit) pieces.push({ text: clean(n.nodeValue ?? ''), r: range.getBoundingClientRect(), el, block: el.closest(BLOCK) });
  }

  return { page: { path: location.pathname, title: pageTitle() }, headings: headingsFor(pieces.flatMap((p) => (p.el ? [p.el] : []))), lines: toLines(pieces), bounds };
}

/**
 * Pieces read back as lines, as a reader would: each card, table row or list item on its own (so figures from cards
 * side by side don't run together), blocks top to bottom and left to right, and inside a block row by row, with wide
 * gaps as column breaks.
 */
export function toLines(pieces: Piece[]): RegionLine[] {
  const groups = new Map<Element | null | undefined, Piece[]>();
  for (const p of pieces) groups.set(p.block, [...(groups.get(p.block) ?? []), p]);
  const blocks = [...groups.values()]
    .map((g) => ({ g, top: Math.min(...g.map((p) => p.r.top)), left: Math.min(...g.map((p) => p.r.left)) }))
    .sort((a, b) => (Math.abs(a.top - b.top) <= 8 ? a.left - b.left : a.top - b.top));
  const rows: Piece[][] = [];
  for (const { g } of blocks) {
    const sorted = [...g].sort((a, b) => a.r.top + a.r.height / 2 - (b.r.top + b.r.height / 2) || a.r.left - b.r.left);
    let first = true;
    for (const p of sorted) {
      const row = first ? undefined : rows[rows.length - 1];
      first = false;
      const mid = p.r.top + p.r.height / 2;
      const prev = row?.[0];
      if (row && prev && !p.whole && !prev.whole && Math.abs(mid - (prev.r.top + prev.r.height / 2)) <= Math.max(4, Math.min(p.r.height, prev.r.height) / 2)) row.push(p);
      else rows.push([p]);
    }
  }
  const lines: RegionLine[] = [];
  let chars = 0;
  for (const row of rows) {
    row.sort((a, b) => a.r.left - b.r.left);
    let text = '';
    let prev: Piece | undefined;
    for (const p of row) {
      const gap = prev ? p.r.left - prev.r.right : 0;
      // The same text drawn twice in one place (a layered label) is read once.
      if (prev && p.text === prev.text && gap < 0) continue;
      text += !prev ? p.text : gap > 18 ? ` | ${p.text}` : gap > 1 ? ` ${p.text}` : p.text;
      prev = p;
    }
    text = text.slice(0, 600);
    if (!text || lines[lines.length - 1]?.text === text) continue;
    if (lines.length >= MAX_LINES || chars + text.length > MAX_CHARS) break;
    chars += text.length;
    const x0 = Math.min(...row.map((p) => p.r.left));
    const y0 = Math.min(...row.map((p) => p.r.top));
    const x1 = Math.max(...row.map((p) => p.r.right));
    const y1 = Math.max(...row.map((p) => p.r.bottom));
    lines.push({ id: lines.length + 1, text, box: { x: x0, y: y0, w: x1 - x0, h: y1 - y0 } });
  }
  return lines;
}

function pageTitle() {
  return clean(document.querySelector('main h1')?.textContent ?? '') || document.title;
}

/** Card titles (with their subtitles) around the circled pieces, and the tabs or ranges selected on the page. */
function headingsFor(els: Element[]): string[] {
  const out: string[] = [];
  const add = (s: string | null | undefined) => {
    const t = clean(s ?? '').slice(0, 160);
    if (t && !out.includes(t)) out.push(t);
  };
  add(pageTitle());
  for (const el of els) {
    const header = el.closest('section')?.querySelector(':scope > header');
    if (header) add([header.querySelector('h2')?.textContent, header.querySelector('p')?.textContent].filter(Boolean).join(': '));
    if (out.length >= 8) break;
  }
  for (const sel of document.querySelectorAll('main [role=tab][aria-selected=true], main [aria-pressed=true]')) {
    add(`Selected: ${sel.textContent ?? ''}`);
    if (out.length >= 11) break;
  }
  return out;
}

/** A loop drawn around an element: an ellipse just outside its box, a little uneven, as a pen would draw it. */
export function loopAround(r: Box, steps = 48): Pt[] {
  const cx = r.x + r.w / 2;
  const cy = r.y + r.h / 2;
  const rx = r.w / 2 + Math.min(24, 8 + r.w * 0.08);
  const ry = r.h / 2 + Math.min(18, 6 + r.h * 0.2);
  const pts: Pt[] = [];
  // A pen starts top left and overshoots the start a little.
  for (let i = 0; i <= steps + 4; i++) {
    const t = -Math.PI * 0.75 + (i / steps) * Math.PI * 2;
    // One slow swell rather than ripples, and the end lands a little outside the start.
    const wobble = 1 + 0.035 * Math.sin(t * 2 + 0.6) + (i / steps) * 0.03;
    pts.push({ x: cx + rx * wobble * Math.cos(t), y: cy + ry * wobble * Math.sin(t) });
  }
  return pts;
}

/** The element a single click points at, widened to the block it belongs to (a stat, a table row, a chart). */
export function blockAt(x: number, y: number, skip: (el: Element) => boolean): Element | null {
  // Under the circle's own layers (the drawing surface while the lasso button is on).
  let el = document.elementsFromPoint(x, y).find((e) => !skip(e)) ?? null;
  if (!el) return null;
  const limit = (window.innerWidth * window.innerHeight) / 2;
  const block = el.closest('[data-explain], tr, li, dl > div, .glass, section');
  if (block) {
    const r = block.getBoundingClientRect();
    if (r.width * r.height < limit) el = block;
  }
  return el;
}

/** Simplify a drawn path: drop points closer than `min` pixels to the last one kept. */
export function thin(pts: Pt[], min = 3): Pt[] {
  const out: Pt[] = [];
  for (const p of pts) {
    const last = out[out.length - 1];
    if (!last || Math.hypot(p.x - last.x, p.y - last.y) >= min) out.push(p);
  }
  return out;
}

/** A smooth SVG path through the points (Catmull-Rom as cubic Béziers). */
export function smoothPath(pts: Pt[], close = false): string {
  if (pts.length < 2) return '';
  const p = close ? [pts[pts.length - 1]!, ...pts, pts[0]!, pts[1]!] : [pts[0]!, ...pts, pts[pts.length - 1]!];
  let d = `M${p[1]!.x.toFixed(1)},${p[1]!.y.toFixed(1)}`;
  for (let i = 1; i < p.length - 2; i++) {
    const [a, b, c, e] = [p[i - 1]!, p[i]!, p[i + 1]!, p[i + 2]!];
    const c1 = { x: b.x + (c.x - a.x) / 6, y: b.y + (c.y - a.y) / 6 };
    const c2 = { x: c.x - (e.x - b.x) / 6, y: c.y - (e.y - b.y) / 6 };
    d += `C${c1.x.toFixed(1)},${c1.y.toFixed(1)} ${c2.x.toFixed(1)},${c2.y.toFixed(1)} ${c.x.toFixed(1)},${c.y.toFixed(1)}`;
  }
  return close ? `${d}Z` : d;
}
